
import React from 'react'
import { debounce } from 'widgets-shared/utilities'
import useEventListener from 'smartstream-widgets/hooks/useEventListener/useEventListener'
import { withComponentContext } from 'widgets-shared/core'
import { Grid, Typography } from '@mui/material'
import { compose } from 'widgets-shared/core'
import LogoFlag from 'smartstream-widgets/controls/LogoFlag/LogoFlag'
import MatchState from 'smartstream-widgets/controls/MatchState/MatchState'
import HelperInfo from 'smartstream-widgets/controls/HelperInfo/HelperInfo'
import ScoreboardAndRecords from 'smartstream-widgets/components/ScoreboardAndRecords/ScoreboardAndRecords'
import { findCompetitorBySide } from 'smartstream-widgets/utils/competitors'

import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import {
  CompetitorBrand,
  CompetitorWithFixtureData,
  Records,
  Side
} from 'smartstream-widgets/types/competitor'
import { Scoreboard } from 'smartstream-widgets/components/ScoreboardAndRecords/types'
import { MatchState as MatchStateType } from 'smartstream-widgets/types/sport'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

export type HeaderInfo = {
  title: string,
  helperInfo: {
    [K in string]: string
  }
}

type Props = {
  classes: { [K in string]: string },
  competitors: Array<CompetitorWithFixtureData>,
  config: {
    BASE_ASSET_PATH: string
  },
  containerRef: React.RefObject<HTMLDivElement>,
  matchState?: MatchStateType,
  scheduledStartTime: string,
  headerInfo: HeaderInfo,
  logoFlagOverrides: {
    logoSize: number
  },
  showNames?: boolean,
  isAwayTeamFirst?: boolean,
  venueName: string,
  hideLiveScore?: boolean
}

const styles = (theme: Theme) => createStyles({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row'
  },
  content: {
    flex: 1,
    width: 'unset',
    height: 82,
    paddingTop: theme.spacing(2)
  },
  dateCaption: {
    color: theme.palette.text.secondary,
    fontWeight: 'bold',
  },
  scoreContainer: {
    padding: `5px 20px 10px`
  },
  matchState: {
    '& p': {
      fontSize: 9
    }
  },
  liveCaption: {
    color: theme.palette.text.secondary,
    fontFamily: `${theme.typography['variantFontFamily']},${theme.typography['fontFamily']}`,
    fontWeight: 'bold',
    textTransform: 'uppercase'
  },
  venueCaption: {},
  logoFlag: {},
  title: {
    alignItems: 'center',
    color: theme.palette.text.primary,
    display: 'flex',
    fontFamily: `${theme.typography['variantFontFamily']},${theme.typography['fontFamily']}`,
    fontSize: theme.typography['fontSize'],
    fontWeight: 'bold',
    textTransform: 'uppercase'
  },
  helperInfoButton: {
    color: theme.palette.text.secondary,
    cursor: 'pointer'
  },
  helperInfoDisclaimer: {
    fontStyle: 'italic',
    marginTop: 15
  },
  helperInfoFooter: {
    bottom: 5,
    left: 0,
    position: 'absolute'
  },
  helperInfoFooterLogo: {
    height: 15,
    marginRight: 5,
    width: 15
  },
  helperInfoFooterText: {
    color: theme.palette.text.secondary,
    fontSize: 9
  },
  helperInfoModal: {
    '& > div:nth-child(3)': {
      bottom: 0
    },
    '& p': {
      marginBottom: 5
    }
  },
  helperInfoTitle: {
    fontWeight: 'bold'
  },
  teamName: {},
  liveScore: {},
  logoFlagSize: {},
  logoLeft: {},
  logoRight: {},
  isLive: {}
})

export function buildOptions(competitors: Array<CompetitorWithFixtureData>, showName: boolean, side: Side) {
  const competitor = (findCompetitorBySide(competitors, side) || {} as CompetitorWithFixtureData)
  const records = competitor.records || {} as Records
  return {
    scores: {
      leftScore: records && records.wins,
      rightScore: records && records.losses,
      animationDuration: 0
    },
    hasRecords: Number.isInteger(records.wins) && Number.isInteger(records.losses),
    showName,
    competitor
  }
}

export function hasBrandElements(brand: CompetitorBrand) {
  const { primaryColor, secondaryColor, logo } = brand || {}
  return !!((primaryColor && secondaryColor) || logo)
}

export function FixtureHeader(props: Props) {
  const {
    classes,
    competitors,
    config: { BASE_ASSET_PATH },
    containerRef,
    headerInfo = {} as HeaderInfo,
    logoFlagOverrides,
    matchState = {} as MatchStateType,
    scheduledStartTime,
    showNames = true,
    isAwayTeamFirst = false,
    venueName,
    hideLiveScore = false
  } = props

  const awayTeam = buildOptions(competitors, showNames, 'AWAY')
  const homeTeam = buildOptions(competitors, showNames, 'HOME')

  const svgKeys = [
    `${awayTeam.competitor.competitorId}-${homeTeam.competitor.competitorId}`,
    `${homeTeam.competitor.competitorId}-${awayTeam.competitor.competitorId}`
  ]

  const leftTeam = isAwayTeamFirst ? awayTeam : homeTeam
  const rightTeam = isAwayTeamFirst ? homeTeam : awayTeam

  const recordsAvailable = leftTeam.hasRecords && rightTeam.hasRecords

  const hasLiveScore = Number.isInteger(matchState.scoreAway) && Number.isInteger(matchState.scoreHome)

  const matchStatus = (matchState && (matchState.matchStatus || matchState.phase)) || 'PreMatch'

  const shouldRenderFlag = hasBrandElements(leftTeam.competitor.brand) && hasBrandElements(rightTeam.competitor.brand)

  const scoreboardAndRecordsProps: Scoreboard = {
    leftTeam,
    rightTeam,
    liveScore: {
      leftScore: matchState && (isAwayTeamFirst ? matchState.scoreAway : matchState.scoreHome),
      rightScore: matchState && (isAwayTeamFirst ? matchState.scoreHome : matchState.scoreAway),
      highlight: 'higher'
    },
    isLive: matchStatus === 'InPlay',
    hideLiveScore
  }

  const helperInfoWithContent = headerInfo.helperInfo && (
    <HelperInfo
      anchorEl={containerRef && containerRef.current}
      classes={{
        closeButton: classes.helperInfoButton,
        modal: classes.helperInfoModal,
        openButton: classes.helperInfoButton,
        title: classes.helperInfoTitle
      }}
      closeIcon={`${BASE_ASSET_PATH}/img/icons.svg#ic-close`}
      openIcon={`${BASE_ASSET_PATH}/img/icons.svg#ic-message-info`}
      title={headerInfo.helperInfo.title}
    >
      <Typography gutterBottom>{headerInfo.helperInfo.content}</Typography>
      <Typography className={classes.helperInfoDisclaimer} gutterBottom>{headerInfo.helperInfo.disclaimer}</Typography>
      <Grid container className={classes.helperInfoFooter} justifyContent='center'>
        <img
          className={classes.helperInfoFooterLogo}
          src={`${BASE_ASSET_PATH}/img/genius-sports-logos/gs-logo-footer-25px.png`}
          alt='Genius Sports Media' />
        <Typography className={classes.helperInfoFooterText}>{headerInfo.helperInfo.footerText}</Typography>
      </Grid>
    </HelperInfo>
  )

  const headerRef: { current: HTMLElement } = React.useRef()
  const [compactDateFormat, setCompactDateFormat] = React.useState(false)

  const useReference = React.useCallback((node: HTMLElement) => {
    headerRef.current = node
    debouncedHandleResize()
  }, [])

  const debouncedHandleResize = debounce(() => {
    setCompactDateFormat(headerRef.current && headerRef.current.clientWidth < 420) 
  }, 200)

  useEventListener('resize', debouncedHandleResize)

  return (
    <Grid container className={classes.root} direction='column' wrap='nowrap' ref={useReference} data-testid='fixture-header-container'>
      {
        shouldRenderFlag &&
        <LogoFlag
          classes={{
            logoFlagSize: classes.logoFlagSize,
            logo: classes.logoLeft
          }}
          hasGradient
          className={classes.logoFlag}
          overrides={logoFlagOverrides}
          competitorsBrand={[leftTeam.competitor.brand]}
          svgKey={svgKeys[0]}
        />
      }
      <Grid
        container
        alignItems='center'
        className={`${classes.content} ${matchStatus === 'InPlay' && classes.isLive}`}
        direction='column'
        justifyContent='space-between'
        wrap='nowrap'>
        {
          headerInfo.title &&
          <Typography className={classes.title}>
            {headerInfo.title}
            {helperInfoWithContent}
          </Typography>
        }
        <MatchState
          classes={{ liveCaption: classes.liveCaption, dateCaption: classes.dateCaption, venueCaption: classes.venueCaption }}
          className={classes.matchState}
          matchPhase={matchState.phase}
          matchStatus={matchStatus}
          scheduledStartTime={scheduledStartTime}
          venueName={venueName}
          compactDateFormat={compactDateFormat}
        />
        {
          (recordsAvailable || hasLiveScore) &&
          <ScoreboardAndRecords
            classes={{
              liveScoreClass: classes.liveScore,
              root: classes.scoreContainer,
              teamName: classes.teamName
            }}
            {...scoreboardAndRecordsProps}
          />
        }
      </Grid>
      {
        shouldRenderFlag &&
        <LogoFlag
          classes={{
            logoFlagSize: classes.logoFlagSize,
            logo: classes.logoRight
          }}
          flip
          hasGradient
          className={classes.logoFlag}
          overrides={logoFlagOverrides}
          competitorsBrand={[rightTeam.competitor.brand]}
          svgKey={svgKeys[1]}
        />
      }
    </Grid>
  )
}

export default compose(
  withComponentContext,
  withStyles(styles, { name: 'FixtureHeader', withTheme: true, defaultTheme: getDefaultTheme })
)(FixtureHeader)
